import { computed, ref, watch } from "vue";
import { TERMINAL_STATUSES } from "@/components/voice_ai/constants";
import { benefitsVerificationService, } from "@/services/benefitsVerificationService";
function yesNo(value, yes, no) {
    if (value === true)
        return { value: yes, tone: "yes" };
    if (value === false)
        return { value: no, tone: "no" };
    return { value: "Unknown", tone: "" };
}
function joinPair(a, b) {
    return [a, b].filter(Boolean).join(" / ");
}
/** Load, confirm and save benefits verification records for one case anchor. */
export function useBenefitsVerification(anchor, options) {
    const records = ref(options.external?.() ?? []);
    const loading = ref(false);
    const confirming = ref(false);
    const error = ref("");
    const dialogOpen = ref(false);
    const lastCallStatus = new Map();
    let knownLatestId;
    let fetchVersion = 0;
    const latest = computed(() => records.value[0] || null);
    const status = computed(() => {
        switch (latest.value?.status) {
            case "VERIFIED":
                return { key: "verified", label: "Verified" };
            case "DRAFT":
                return { key: "draft", label: "AI draft, needs confirmation" };
            case "NEEDS_REVERIFY":
                return { key: "reverify", label: "Low confidence, re-verify" };
            default:
                return { key: "none", label: "Not started" };
        }
    });
    const headline = computed(() => {
        const r = latest.value;
        if (!r)
            return [];
        const d = r.details || {};
        const pa = yesNo(r.priorAuthRequired, "Required", "Not required");
        return [
            { key: "coverage", label: "Coverage", ...yesNo(r.coverageActive, "Active", "Inactive") },
            { key: "network", label: "Network", ...yesNo(r.inNetwork, "In network", "Out of network") },
            {
                key: "pa",
                label: "Prior auth",
                value: pa.value + (d.priorAuthEntity ? ` via ${d.priorAuthEntity}` : ""),
                tone: pa.tone,
            },
        ];
    });
    const money = computed(() => {
        const d = latest.value?.details || {};
        const rows = [
            { key: "deductible", label: "Deductible", value: joinPair(d.deductibleIndividual, d.deductibleFamily), tone: "" },
            {
                key: "oop",
                label: "Out-of-pocket max",
                value: joinPair(d.outOfPocketIndividual, d.outOfPocketFamily),
                tone: "",
            },
            { key: "copay", label: "Copay", value: d.copay || "", tone: "" },
            { key: "coinsurance", label: "Coinsurance", value: d.coinsurance || "", tone: "" },
        ];
        return rows.filter((f) => f.value);
    });
    const plan = computed(() => {
        const r = latest.value;
        if (!r)
            return [];
        const rows = [
            { key: "plan", label: "Plan", value: r.planName, tone: "" },
            { key: "payer", label: "Payer", value: r.payerName, tone: "" },
            { key: "member", label: "Member ID", value: r.memberId, tone: "" },
        ];
        return rows.filter((f) => f.value);
    });
    const services = computed(() => latest.value?.details?.services || []);
    const provenance = computed(() => {
        const r = latest.value;
        if (!r)
            return null;
        const d = r.details || {};
        return {
            source: r.source === "voice_ai" ? "AI call" : `Entered by ${r.createdByName || "staff"}`,
            score: r.source === "voice_ai" ? r.score : null,
            rep: d.repName || "",
            reference: d.callReferenceNumber || "",
            verifiedBy: r.status === "VERIFIED" ? r.verifiedByName || "staff" : "",
            verifiedAt: r.status === "VERIFIED" && r.verifiedAt ? new Date(r.verifiedAt) : null,
            notes: d.notes || "",
        };
    });
    async function load() {
        if (options.external?.() !== undefined) {
            options.onReload?.();
            return;
        }
        const version = ++fetchVersion;
        loading.value = true;
        error.value = "";
        try {
            const next = await benefitsVerificationService.list(anchor.value);
            if (version !== fetchVersion)
                return;
            records.value = next;
            const latestId = latest.value?.id ?? null;
            if (knownLatestId !== undefined && knownLatestId !== latestId)
                options.onChanged(latest.value);
            knownLatestId = latestId;
        }
        catch (err) {
            if (version === fetchVersion)
                error.value = err?.message || "Failed to load benefits";
        }
        finally {
            if (version === fetchVersion)
                loading.value = false;
        }
    }
    async function confirm() {
        if (!latest.value)
            return;
        confirming.value = true;
        error.value = "";
        try {
            const confirmed = await benefitsVerificationService.confirm(latest.value.id);
            records.value = [confirmed, ...records.value.filter((r) => r.id !== confirmed.id)];
            knownLatestId = confirmed.id;
            options.onChanged(confirmed);
        }
        catch (err) {
            error.value = err?.message || "Failed to confirm benefits";
        }
        finally {
            confirming.value = false;
        }
    }
    function onSaved(record) {
        records.value = [record, ...records.value.filter((r) => r.id !== record.id)];
        knownLatestId = record.id;
        options.onChanged(record);
    }
    // A finished AI call drafts a record server-side; reload once per call as it is seen to reach a
    // terminal state. Calls already finished when first seen are covered by the initial load.
    function onCallsChanged(calls) {
        let finished = false;
        for (const call of calls) {
            const previous = lastCallStatus.get(call.id);
            lastCallStatus.set(call.id, call.status);
            if (previous !== undefined && previous !== call.status && TERMINAL_STATUSES.includes(call.status))
                finished = true;
        }
        if (finished)
            load();
    }
    watch(() => `${anchor.value.type}:${anchor.value.id}`, load);
    if (options.external?.() === undefined)
        load();
    watch(() => options.external?.(), (next, previous) => {
        if (next !== undefined)
            records.value = next;
        else if (previous !== undefined)
            load();
    });
    return {
        records,
        latest,
        loading,
        confirming,
        error,
        dialogOpen,
        status,
        headline,
        money,
        plan,
        services,
        provenance,
        load,
        confirm,
        onSaved,
        onCallsChanged,
    };
}
